import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { useCreateMember, useUpdateMember } from "@/hooks/useMembers";
import { useToast } from "@/hooks/useToast";
import type { Tables } from "@/types/database";
import type { UserRole } from "@/types/database";

type Member = Tables<"users">;

interface MemberFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (memberId: string) => void;
  member?: Member;
}

const initialFormData = {
  full_name: "",
  email: "",
  role: "musician" as UserRole,
  phone: "",
};

export function MemberForm({
  open,
  onClose,
  onSuccess,
  member,
}: MemberFormProps) {
  const createMember = useCreateMember();
  const updateMember = useUpdateMember();
  const { toast } = useToast();

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (open) {
      setFormData(
        member
          ? {
              full_name: member.full_name || "",
              email: member.email,
              role: member.role,
              phone: member.phone || "",
            }
          : initialFormData,
      );
    }
  }, [open, member]);

  const isEditing = !!member;
  const isPending = createMember.isPending || updateMember.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await updateMember.mutateAsync({
          id: member.id,
          member: {
            ...formData,
            email: formData.email || undefined,
          },
        });
        toast({ title: "Membre mis à jour" });
        onClose();
        onSuccess?.(member.id);
      } else {
        const newMember = await createMember.mutateAsync({
          id: crypto.randomUUID(),
          ...formData,
          email: formData.email || `member-${crypto.randomUUID()}@temp.local`,
        });
        toast({ title: "Membre créé" });
        setFormData(initialFormData);
        onClose();
        onSuccess?.(newMember.id);
      }
    } catch {
      toast({
        title: "Erreur",
        description: isEditing
          ? "Impossible de mettre à jour le membre"
          : "Impossible de créer le membre",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier le membre" : "Nouveau membre"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="member-name">Nom complet *</Label>
              <Input
                id="member-name"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-email">Email</Label>
              <Input
                id="member-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-phone">Téléphone</Label>
              <Input
                id="member-phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <div className="flex items-center gap-1">
                <Label>Rôle système *</Label>
                <HelpTooltip content="Admin : accès complet. Director : gestion complète. Coordinator : gestion d'événements. Musician : membre du collectif." />
              </div>
              <Select
                value={formData.role}
                onValueChange={(v) =>
                  setFormData({
                    ...formData,
                    role: v as UserRole,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="musician">Musicien</SelectItem>
                  <SelectItem value="coordinator">Coordinateur</SelectItem>
                  <SelectItem value="director">Directeur</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditing
                  ? "Mise à jour..."
                  : "Création..."
                : isEditing
                  ? "Mettre à jour"
                  : "Créer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
